import { useEffect, useRef } from 'react'
import { isMobile } from 'react-device-detect'
export default function ParticlesCursor() {
  const canvasRef = useRef(null)
  const animationRef = useRef(null)
  const particlesRef = useRef([])
  const posRef = useRef({ x: 0, y: 0 })
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    let width = window.innerWidth
    let height = window.innerHeight
    const handleResize = () => {
      width = window.innerWidth
      height = window.innerHeight
      canvas.width = width
      canvas.height = height
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    const createParticles = (clientX, clientY) => {
      const prevX = posRef.current.x || clientX
      const prevY = posRef.current.y || clientY
      posRef.current.x = clientX
      posRef.current.y = clientY
      const vx = clientX - prevX
      const vy = clientY - prevY
      const speed = Math.sqrt(vx * vx + vy * vy)
      const threshold = isMobile ? 2 : 3
      if (speed > threshold) {
        const count = Math.min(Math.floor(speed / 4), 4)
        for (let i = 0; i < count; i++) {
          const angle = Math.atan2(vy, vx) + (Math.random() - 0.5) * 1.2
          const vel = speed * 0.12 * (Math.random() * 0.5 + 0.5)
          particlesRef.current.push({
            x: clientX + (Math.random() - 0.5) * 8,
            y: clientY + (Math.random() - 0.5) * 8,
            vx: Math.cos(angle) * vel,
            vy: Math.sin(angle) * vel,
            size: Math.random() * 2 + 1,
            life: 1,
            decay: Math.random() * 0.012 + 0.008
          })
        }
      }
    }
    const handleMouseMove = (e) => {
      createParticles(e.clientX, e.clientY)
    }
    const handleTouchMove = (e) => {
      if (e.touches.length > 0) {
        const touch = e.touches[0]
        createParticles(touch.clientX, touch.clientY)
      }
    }
    const handleTouchStart = (e) => {
      if (e.touches.length > 0) {
        const touch = e.touches[0]
        posRef.current.x = touch.clientX
        posRef.current.y = touch.clientY
      }
    }
    if (isMobile) {
      window.addEventListener('touchmove', handleTouchMove, { passive: true })
      window.addEventListener('touchstart', handleTouchStart, { passive: true })
    } else {
      window.addEventListener('mousemove', handleMouseMove)
    }
    let frameCount = 0
    const frameSkip = 1  // Har frame - smooth animation
    const animate = () => {
      frameCount++
      if (frameCount % frameSkip !== 0) {
        animationRef.current = requestAnimationFrame(animate)
        return
      }
      ctx.clearRect(0, 0, width, height)
      const particles = particlesRef.current
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i]
        p.x += p.vx * frameSkip
        p.y += p.vy * frameSkip
        p.vx *= Math.pow(0.97, frameSkip)
        p.vy *= Math.pow(0.97, frameSkip)
        p.life -= p.decay * frameSkip
        if (p.life <= 0) {
          particles.splice(i, 1)
          continue
        }
        const alpha = p.life * 0.7
        ctx.beginPath()
        ctx.arc(p.x, p.y, p.size * p.life, 0, Math.PI * 2)
        ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`
        ctx.fill()
      }
      const maxParticles = 100
      if (particles.length > maxParticles) {
        particles.splice(0, particles.length - maxParticles)
      }
      animationRef.current = requestAnimationFrame(animate)
    }
    animate()
    return () => {
      window.removeEventListener('resize', handleResize)
      if (isMobile) {
        window.removeEventListener('touchmove', handleTouchMove)
        window.removeEventListener('touchstart', handleTouchStart)
      } else {
        window.removeEventListener('mousemove', handleMouseMove)
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [])
  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 9999
      }}
    />
  )
}